/**
 * "Sending…" state for every Gravity Form on the site.
 *
 * The theme strips Gravity Forms' own stylesheets (see functions.php), which
 * takes its submit spinner with them: the spinner element is still rendered,
 * but nothing draws it. So a visitor pressing Send saw no change at all for the
 * second or two the request takes -- the button stayed live, inviting a second
 * click, and the form looked broken. Raised in the October 2026 review.
 *
 * This adds `is-sending` to the form's `.awdd-form` wrapper for the duration;
 * blocks.css dims and locks the fields and turns the button's arrow into a
 * spinner. The wrapper is the theme's own element, outside the markup Gravity
 * Forms replaces, so the class survives the swap and has to be taken off here.
 *
 * Gravity Forms starts a send by triggering a jQuery `submit` on the form and
 * then calling form.submit(), which fires no native submit event -- so this
 * listens through jQuery, not addEventListener.
 *
 * The end of a send is the AJAX form's hidden iframe (the form's `target`)
 * firing `load`, which happens exactly when the server's answer arrives. NOT
 * `gform_post_render`: measured in a browser, Gravity Forms fires that once
 * about 140ms after the click, while the request is still in flight, and only
 * again when the answer lands -- keying on it dropped the state almost at once.
 * A non-AJAX form has no such iframe and simply navigates away while still
 * showing the state, which is what it should do. A send that never answers is
 * released after 30s rather than left locked.
 */
const SENDING = 'is-sending';
const TIMEOUT_MS = 30000;

export default function initForms() {
    const $ = window.jQuery;
    if (!$) return;

    const release = wrap => {
        if (!wrap) return;
        wrap.classList.remove(SENDING);
        wrap.removeAttribute('aria-busy');
        clearTimeout(wrap._awddSendTimer);
    };

    $(document).on('submit', '.awdd-form form', function (e) {
        // Gravity Forms' own double-submit guard cancels the second click;
        // a cancelled submit must not re-arm the state.
        if (e.isDefaultPrevented()) return;
        const wrap = this.closest('.awdd-form');
        if (!wrap) return;
        wrap.classList.add(SENDING);
        wrap.setAttribute('aria-busy', 'true');
        clearTimeout(wrap._awddSendTimer);
        wrap._awddSendTimer = setTimeout(() => release(wrap), TIMEOUT_MS);

        // The iframe sits outside the markup Gravity Forms swaps, so it is the
        // same element before and after; listen once per send.
        const frame = this.target ? document.getElementsByName(this.target)[0] || document.getElementById(this.target) : null;
        if (frame) frame.addEventListener('load', () => release(wrap), { once: true });
    });
}
